import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckSquare, ChevronRight, Settings, Settings2 } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { PageHeader, Button, Card, Checkbox, Input, LoadingState, ErrorState } from '@/components/ui'
import { useToast } from '@/components/ui'

interface DispoSettings {
  effortThreshold: number
  runtimeThreshold: number
  shiftStart: string
  shiftEnd: string
  emailSender: string
  minAbsenceWorkdays: number
  customerReactionStatusIds: number[]
}

interface TanssTicketStatus {
  id: number
  name: string
}

const DEFAULT_SETTINGS: DispoSettings = {
  effortThreshold: 70,
  runtimeThreshold: 90,
  shiftStart: '08:00',
  shiftEnd: '17:00',
  emailSender: '',
  minAbsenceWorkdays: 1,
  customerReactionStatusIds: [30],
}

function extractErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    String(err)
  )
}

export function DispoSettingsPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [form, setForm] = useState<DispoSettings>(DEFAULT_SETTINGS)

  const { data, isLoading, error } = useQuery<DispoSettings>({
    queryKey: ['dispo-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/settings')
      return res.data
    },
  })

  const { data: ticketStatusesData, isLoading: statusesLoading, error: statusesError } = useQuery<{ statuses: TanssTicketStatus[] }>({
    queryKey: ['tanss-ticket-statuses'],
    queryFn: async () => {
      const res = await apiClient.get('/dispo/krankenstand/ticket-statuses')
      return res.data
    },
  })

  useEffect(() => {
    if (data) setForm({ ...DEFAULT_SETTINGS, ...data })
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (settings: DispoSettings) => {
      await apiClient.post('/dispo/settings', settings)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispo-settings'] })
      toast({ title: 'Einstellungen gespeichert', variant: 'success' })
    },
    onError: (err) => {
      toast({ title: 'Fehler beim Speichern', description: extractErrorMessage(err), variant: 'error' })
    },
  })

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState message={extractErrorMessage(error)} />

  const ticketStatuses = ticketStatusesData?.statuses ?? []

  function toggleCustomerReactionStatus(id: number) {
    setForm((f) => {
      const current = f.customerReactionStatusIds
      const next = current.includes(id) ? current.filter((v) => v !== id) : [...current, id]
      return { ...f, customerReactionStatusIds: next }
    })
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Dispo-Einstellungen"
        icon={<Settings className="w-5 h-5" />}
      />

      <Card>
        <Card.Header>
          <h2 className="text-h3">Aufwands-Schwellenwerte</h2>
        </Card.Header>
        <Card.Body className="space-y-4">
          <Input
            label="Aufwands-Schwellenwert (%)"
            type="number"
            min={0}
            max={100}
            value={form.effortThreshold}
            onChange={(e) => setForm((f) => ({ ...f, effortThreshold: Number(e.target.value) }))}
            hint="Anteil der Stundenkapazität, ab dem eine Warnung angezeigt wird."
          />
          <Input
            label="Laufzeit-Schwellenwert (%)"
            type="number"
            min={0}
            max={100}
            value={form.runtimeThreshold}
            onChange={(e) => setForm((f) => ({ ...f, runtimeThreshold: Number(e.target.value) }))}
            hint="Ticket-Laufzeitanteil, ab dem eine Prüfung ausgelöst wird."
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h2 className="text-h3">Schicht-Konfiguration</h2>
        </Card.Header>
        <Card.Body className="space-y-4">
          <Input
            label="Schichtbeginn"
            type="time"
            value={form.shiftStart}
            onChange={(e) => setForm((f) => ({ ...f, shiftStart: e.target.value }))}
          />
          <Input
            label="Schichtende"
            type="time"
            value={form.shiftEnd}
            onChange={(e) => setForm((f) => ({ ...f, shiftEnd: e.target.value }))}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h2 className="text-h3">E-Mail-Absender</h2>
        </Card.Header>
        <Card.Body>
          <Input
            label="Absender-E-Mail-Adresse"
            type="email"
            value={form.emailSender}
            onChange={(e) => setForm((f) => ({ ...f, emailSender: e.target.value }))}
            hint="Von dieser Adresse werden automatische Dispo-E-Mails versendet."
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h2 className="text-h3">Krankenstand-Übersicht</h2>
        </Card.Header>
        <Card.Body className="space-y-6">
          <Input
            label="Mindest-Abwesenheit (Arbeitstage)"
            type="number"
            min={1}
            value={form.minAbsenceWorkdays}
            onChange={(e) => setForm((f) => ({ ...f, minAbsenceWorkdays: Math.max(1, Number(e.target.value)) }))}
            hint="Ab wie vielen Arbeitstagen Abwesenheit ein Mitarbeiter in der Ticketliste erscheint. Wochenenden zählen nicht mit."
          />

          <div>
            <p className="text-body font-medium text-gray-900 mb-1">Status für Kundenreaktion erfolgt</p>
            <p className="text-caption text-gray-500 mb-3">
              Tickets mit diesen TANSS-Status werden im Krankenstand-Modul als „Kundenreaktion erfolgt" priorisiert.
            </p>
            {statusesLoading && (
              <p className="text-caption text-gray-400">TANSS-Status werden geladen…</p>
            )}
            {statusesError && (
              <p className="text-caption text-red-500">
                Fehler beim Laden der TANSS-Status: {extractErrorMessage(statusesError)}
              </p>
            )}
            {!statusesLoading && !statusesError && ticketStatuses.length === 0 && (
              <p className="text-caption text-gray-400">Keine TANSS-Status verfügbar.</p>
            )}
            {ticketStatuses.length > 0 && (
              <div className="space-y-2 border border-gray-200 rounded-md p-3 max-h-60 overflow-y-auto">
                {ticketStatuses.map((s) => (
                  <Checkbox
                    key={s.id}
                    label={`${s.name} (${s.id})`}
                    checked={form.customerReactionStatusIds.includes(s.id)}
                    onChange={() => toggleCustomerReactionStatus(s.id)}
                  />
                ))}
              </div>
            )}
            {form.customerReactionStatusIds.length === 0 && (
              <p className="text-caption text-orange-500 mt-2">
                Keine Status ausgewählt – Kategorie „Kundenreaktion erfolgt" bleibt leer.
              </p>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h2 className="text-h3">Weitere Einstellungen</h2>
        </Card.Header>
        <Card.Body className="space-y-2">
          <Link
            to="/einstellungen/dispo/leistungsfreigabe"
            className="flex items-center justify-between p-3 rounded-md hover:bg-gray-50 border border-gray-100 group transition"
          >
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-brand-700" />
              <div>
                <p className="text-sm font-medium text-gray-900">Leistungsfreigabe</p>
                <p className="text-caption text-gray-500">
                  Freigabekriterien, Fehlerprüfungen und Übersteuerungsregeln pro Mandant konfigurieren
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition" />
          </Link>
          <Link
            to="/einstellungen/dispo/dienste"
            className="flex items-center justify-between p-3 rounded-md hover:bg-gray-50 border border-gray-100 group transition"
          >
            <div className="flex items-center gap-3">
              <Settings2 className="w-5 h-5 text-brand-700" />
              <div>
                <p className="text-sm font-medium text-gray-900">Dienstdefinitionen</p>
                <p className="text-caption text-gray-500">
                  Globale Dienste, Schichtregeln und TANSS-Planungstypen für die Wochenplanung (nur Admin)
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition" />
          </Link>
        </Card.Body>
      </Card>

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={() => saveMutation.mutate(form)}
          loading={saveMutation.isPending}
        >
          Einstellungen speichern
        </Button>
      </div>
    </div>
  )
}
